/**
 * CJPApp — Cockroach Janta Party mock 2029 poll.
 * Visual design faithfully replicated from the original cjp_poll reference file:
 * cream/paper/ink risograph aesthetic, Archivo Black font, hard box-shadows,
 * rubber-stamp decorations, grain overlay.
 *
 * Data layer wired to Supabase (votes table, state_id='cjp') with seed baseline.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import {
  Check, Share2, Download, X, TrendingUp, MapPin,
  AlertCircle, Loader2, BookOpen, ExternalLink, ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { castVote, cacheVote, fetchCounts } from "../storage";
import SubscribeCTA from "../components/SubscribeCTA";

// ── Theme ─────────────────────────────────────────────────────────────────────
const K = {
  cream:   "#F4EFE0",
  paper:   "#EAE3CE",
  ink:     "#0A0A0A",
  inkSoft: "#3D3A2E",
  saffron: "#FF6B1A",
  green:   "#0E7C3A",
  red:     "#D62828",
  navy:    "#1A2A4A",
  roach:   "#5B3A1F",
  yellow:  "#F2C94C",
};

const MANIFESTO_URL = "https://cockroachjantaparty.org/#manifesto";

// ── Parties ───────────────────────────────────────────────────────────────────
const PARTIES = [
  { id: "CJP", en: "Cockroach Janta Party",    hi: "कॉकरोच जनता पार्टी",    short: "CJP",    color: K.roach,   featured: true, tag: "🪳 the people's roach" },
  { id: "BJP", en: "Bharatiya Janata Party",   hi: "भारतीय जनता पार्टी",    short: "BJP",    color: "#FF9933" },
  { id: "INC", en: "Indian National Congress", hi: "इंडियन नेशनल कांग्रेस", short: "INC",    color: "#138808" },
  { id: "AAP", en: "Aam Aadmi Party",          hi: "आम आदमी पार्टी",        short: "AAP",    color: "#0066B3" },
  { id: "SP",  en: "Samajwadi Party",          hi: "समाजवादी पार्टी",       short: "SP",     color: "#C8102E" },
  { id: "TMC", en: "Trinamool Congress",       hi: "तृणमूल कांग्रेस",       short: "TMC",    color: "#1B5E20" },
  { id: "DMK", en: "Dravida Munnetra Kazhagam",hi: "द्रविड़ मुनेत्र कड़गम",  short: "DMK",    color: "#555555" },
  { id: "TVK", en: "Tamilaga Vettri Kazhagam", hi: "तमिलगा वेत्री कषगम",   short: "TVK",    color: "#B7141C" },
  { id: "TDP", en: "Telugu Desam Party",       hi: "तेलुगु देशम पार्टी",    short: "TDP",    color: "#D4A800" },
  { id: "JDU", en: "Janata Dal (United)",      hi: "जनता दल (यूनाइटेड)",   short: "JD(U)",  color: "#3D5A1A" },
];
const PARTY_BY_ID = Object.fromEntries(PARTIES.map(p => [p.id, p]));

// ── States & UTs ──────────────────────────────────────────────────────────────
const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman & Nicobar","Chandigarh","Dadra & Nagar Haveli",
  "Delhi","Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry",
];

// ── Empty tally skeleton (all zeros — real data comes from Supabase) ──────────
function emptyTally() {
  const t = {};
  PARTIES.forEach(p => { t[p.id] = { _total: 0, byState: {} }; });
  return t;
}

// ── Data helpers ──────────────────────────────────────────────────────────────
function readLocalVote() {
  try {
    const v = localStorage.getItem("cjp:user_vote");
    return v ? JSON.parse(v) : null;
  } catch (_) { return null; }
}

/** Convert Supabase { state: { partyId: N } } to original { partyId: { _total, byState } } */
function supabaseToTally(counts) {
  const t = {};
  PARTIES.forEach(p => { t[p.id] = { _total: 0, byState: {} }; });
  Object.entries(counts).forEach(([state, parties]) => {
    Object.entries(parties).forEach(([pid, count]) => {
      if (!t[pid]) t[pid] = { _total: 0, byState: {} };
      t[pid]._total += count;
      t[pid].byState[state] = (t[pid].byState[state] || 0) + count;
    });
  });
  return t;
}


// ── Audio click ───────────────────────────────────────────────────────────────
let _audioCtx = null;
function playClickSound() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime;

    const thump = ctx.createOscillator();
    const tg    = ctx.createGain();
    thump.type = "square";
    thump.frequency.setValueAtTime(180, t0);
    thump.frequency.exponentialRampToValueAtTime(70, t0 + 0.06);
    tg.gain.setValueAtTime(0.0001, t0);
    tg.gain.exponentialRampToValueAtTime(0.35, t0 + 0.005);
    tg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
    thump.connect(tg).connect(ctx.destination);
    thump.start(t0); thump.stop(t0 + 0.1);

    const tick = ctx.createOscillator();
    const tkg  = ctx.createGain();
    tick.type = "triangle";
    tick.frequency.setValueAtTime(2200, t0);
    tick.frequency.exponentialRampToValueAtTime(1400, t0 + 0.03);
    tkg.gain.setValueAtTime(0.0001, t0);
    tkg.gain.exponentialRampToValueAtTime(0.22, t0 + 0.003);
    tkg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
    tick.connect(tkg).connect(ctx.destination);
    tick.start(t0); tick.stop(t0 + 0.06);
  } catch (_) {}
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = n => new Intl.NumberFormat("en-IN").format(n);
const pct = (part, total) => total ? Math.round((part / total) * 1000) / 10 : 0;

// ── CJP SVG logo ─────────────────────────────────────────────────────────────
function CJPLogo({ size = 48, style }) {
  return (
    <svg
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="CJP logo"
      width={size}
      height={size}
      style={{ display: "block", flexShrink: 0, ...style }}
    >
      <circle cx="32" cy="32" r="29" fill="none" stroke="#E0651E" strokeWidth="3" strokeDasharray="46 1000" transform="rotate(-90 32 32)" />
      <circle cx="32" cy="32" r="29" fill="none" stroke="#1F5A2E" strokeWidth="3" strokeDasharray="46 1000" transform="rotate(30 32 32)" />
      <circle cx="32" cy="32" r="29" fill="none" stroke="#2A1A10" strokeWidth="0.8" />
      <ellipse cx="32" cy="36" rx="11" ry="16" fill="#5A2F12" />
      <ellipse cx="32" cy="25" rx="7" ry="6" fill="#5A2F12" />
      <path d="M28 17 Q22 10 18 8 M36 17 Q42 10 46 8" stroke="#2A1A10" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <rect x="26" y="23" width="12" height="3.5" rx="1" fill="#0a0807" />
    </svg>
  );
}

// ── Grain overlay ─────────────────────────────────────────────────────────────
function GrainOverlay() {
  return (
    <svg style={{
      position: "fixed", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none", opacity: 0.18, mixBlendMode: "multiply", zIndex: 1,
    }}>
      <filter id="cjp-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
        <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.6 0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#cjp-grain)" />
    </svg>
  );
}

// ── Rubber stamp ──────────────────────────────────────────────────────────────
function Stamp({ children, color = K.red, rotate = -8, style }) {
  return (
    <div style={{
      display:       "inline-block",
      border:        `3px solid ${color}`,
      color,
      padding:       "6px 14px",
      fontFamily:    "'Archivo Black', sans-serif",
      fontSize:      "0.78rem",
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      transform:     `rotate(${rotate}deg)`,
      opacity:       0.85,
      background:    "rgba(244,239,224,0.4)",
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Party card (vote screen) ──────────────────────────────────────────────────
function PartyCard({ party, selected, onClick }) {
  const [pressed, setPressed] = useState(false);
  const isCJP = party.id === "CJP";

  return (
    <button
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        position:   "relative",
        width:      "100%",
        textAlign:  "left",
        background: selected ? party.color : K.paper,
        color:      selected ? K.cream : K.ink,
        border:     `3px solid ${K.ink}`,
        borderRadius: 0,
        boxShadow:  pressed
          ? `2px 2px 0 ${K.ink}`
          : selected ? `8px 8px 0 ${K.ink}` : `5px 5px 0 ${K.ink}`,
        transform:  pressed ? "translate(3px, 3px)" : "translate(0, 0)",
        transition: "transform 80ms ease, box-shadow 80ms ease, background 200ms ease",
        cursor:     "pointer",
        fontFamily: "'IBM Plex Sans', sans-serif",
        outline:    "none",
        padding:    0,
      }}
    >
      {/* ── Full layout (desktop) ─────────────────────────────── */}
      <div
        className="cjp-party-full"
        style={{
          padding:    isCJP ? "1.6rem 1.3rem" : "1.1rem 1.3rem",
          alignItems: "center",
          gap:        "1rem",
          width:      "100%",
        }}
      >
        {isCJP ? (
          <div style={{
            width: 64, height: 64,
            background: K.cream,
            border: `3px solid ${K.ink}`,
            flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 6,
          }}>
            <CJPLogo size={48} />
          </div>
        ) : (
          <div style={{
            width: 40, height: 40,
            background: party.color,
            border: `3px solid ${K.ink}`,
            flexShrink: 0,
          }} />
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily:    "'Archivo Black', sans-serif",
            fontSize:      isCJP ? "1.5rem" : "1.15rem",
            lineHeight:    1,
            letterSpacing: "-0.01em",
            textTransform: "uppercase",
          }}>
            {party.short}
          </div>
          <div style={{ fontSize: isCJP ? "0.85rem" : "0.78rem", marginTop: 4, opacity: 0.85 }}>
            {party.en}
          </div>
          <div style={{ fontFamily: "'Noto Sans Devanagari', sans-serif", fontSize: "0.78rem", marginTop: 2, opacity: 0.75 }}>
            {party.hi}
          </div>
          {isCJP && party.tag && (
            <div style={{
              fontFamily:    "'Archivo Black', sans-serif",
              fontSize:      "0.7rem",
              marginTop:     8,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color:         selected ? K.yellow : K.red,
            }}>
              {party.tag}
            </div>
          )}
          {isCJP && (
            <a
              href={MANIFESTO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display:        "inline-flex",
                alignItems:     "center",
                gap:            6,
                marginTop:      10,
                padding:        "5px 12px",
                background:     "transparent",
                color:          selected ? K.cream : K.roach,
                border:         `2px solid ${selected ? K.cream + "88" : K.roach}`,
                fontFamily:     "'Archivo Black', sans-serif",
                fontSize:       "0.65rem",
                letterSpacing:  "0.1em",
                textTransform:  "uppercase",
                textDecoration: "none",
                transition:     "opacity 150ms ease",
              }}
            >
              <BookOpen size={11} />
              Manifesto
              <ExternalLink size={10} style={{ opacity: 0.7 }} />
            </a>
          )}
        </div>

        {selected && (
          <div style={{
            width: 36, height: 36,
            background: K.cream,
            border: `3px solid ${K.ink}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Check size={20} strokeWidth={4} color={K.ink} />
          </div>
        )}
      </div>

      {/* ── Compact layout (mobile, 3-col grid) ──────────────── */}
      <div
        className="cjp-party-compact"
        style={{
          flexDirection: "column",
          alignItems:    "center",
          justifyContent:"center",
          gap:           6,
          padding:       isCJP ? "0.9rem 0.5rem" : "0.75rem 0.4rem",
          width:         "100%",
          minHeight:     isCJP ? 90 : 76,
          textAlign:     "center",
          position:      "relative",
        }}
      >
        {/* Color bar at top */}
        <div style={{
          position:   "absolute",
          top: 0, left: 0, right: 0,
          height:     4,
          background: isCJP ? `linear-gradient(90deg, ${K.roach}, ${K.saffron})` : party.color,
        }} />

        {/* Logo / color dot */}
        {isCJP ? (
          <CJPLogo size={32} />
        ) : (
          <div style={{
            width: 20, height: 20,
            background:  party.color,
            border:      `2px solid ${K.ink}`,
            flexShrink:  0,
            borderRadius: 2,
          }} />
        )}

        {/* Short name */}
        <div style={{
          fontFamily:    "'Archivo Black', sans-serif",
          fontSize:      isCJP ? "0.95rem" : "0.82rem",
          lineHeight:    1,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}>
          {party.short}
        </div>

        {/* Manifesto link — CJP compact only */}
        {isCJP && (
          <a
            href={MANIFESTO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{
              display:        "inline-flex",
              alignItems:     "center",
              gap:            4,
              padding:        "3px 8px",
              background:     "transparent",
              color:          selected ? K.cream : K.roach,
              border:         `1.5px solid ${selected ? K.cream + "88" : K.roach}`,
              fontFamily:     "'Archivo Black', sans-serif",
              fontSize:       "0.55rem",
              letterSpacing:  "0.08em",
              textTransform:  "uppercase",
              textDecoration: "none",
            }}
          >
            <BookOpen size={9} /> Manifesto
          </a>
        )}

        {/* Selected tick */}
        {selected && (
          <div style={{
            position:   "absolute",
            top: 6, right: 6,
            width: 18, height: 18,
            background: K.ink,
            display:    "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Check size={11} strokeWidth={3.5} color={K.cream} />
          </div>
        )}
      </div>
    </button>
  );
}

// ── Result bar ────────────────────────────────────────────────────────────────
function ResultBar({ party, count, total, isMine, rank }) {
  const p     = pct(count, total);
  const isCJP = party.id === "CJP";
  return (
    <div style={{
      display:               "grid",
      gridTemplateColumns:   "32px 1fr 90px",
      gap:                   12,
      alignItems:            "center",
      padding:               "10px 0",
      borderTop:             `2px solid ${K.ink}`,
    }}>
      <div style={{
        fontFamily: "'Archivo Black', sans-serif",
        fontSize:   "1.2rem",
        color:      rank === 1 ? K.red : K.inkSoft,
      }}>
        {rank}
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <span style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: "1rem", letterSpacing: "0.02em" }}>
            {party.short}
          </span>
          {isCJP && <CJPLogo size={18} style={{ display: "inline-block", verticalAlign: "middle", marginBottom: 2 }} />}
          {isMine && (
            <span style={{
              background: K.ink, color: K.yellow,
              fontSize: "0.6rem", letterSpacing: "0.16em",
              padding: "2px 6px", fontFamily: "'Archivo Black', sans-serif",
              textTransform: "uppercase",
            }}>
              You
            </span>
          )}
          <span style={{ fontSize: "0.72rem", color: K.inkSoft, marginLeft: "auto" }}>
            {fmt(count)}
          </span>
        </div>
        <div style={{
          height:   14,
          background: K.cream,
          border:   `2px solid ${K.ink}`,
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{
            position:    "absolute",
            top: 0, left: 0, bottom: 0,
            width:       `${p}%`,
            background:  party.color,
            transition:  "width 600ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            backgroundImage: isCJP
              ? `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.15) 4px, rgba(0,0,0,0.15) 8px)`
              : "none",
          }} />
        </div>
      </div>
      <div style={{
        fontFamily: "'Archivo Black', sans-serif",
        fontSize:   "1.4rem",
        textAlign:  "right",
        color:      rank === 1 ? K.red : K.ink,
      }}>
        {p.toFixed(1)}%
      </div>
    </div>
  );
}

// ── State heatmap ─────────────────────────────────────────────────────────────
function StateHeatmap({ tally, myVote }) {
  const rows = useMemo(() => {
    return STATES.map(s => {
      let total = 0, cjp = 0;
      PARTIES.forEach(p => {
        const v = tally[p.id]?.byState?.[s] || 0;
        total += v;
        if (p.id === "CJP") cjp = v;
      });
      return { state: s, total, cjp, share: pct(cjp, total) };
    }).sort((a, b) => b.share - a.share);
  }, [tally]);

  const max = Math.max(...rows.map(r => r.share), 1);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <h3 style={{
          fontFamily:    "'Archivo Black', sans-serif",
          fontSize:      "1.4rem",
          textTransform: "uppercase",
          letterSpacing: "0.01em",
          margin:        0,
        }}>
          CJP support by state
        </h3>
        <span style={{ fontSize: "0.72rem", color: K.inkSoft, fontStyle: "italic" }}>
          🪳 = roach country
        </span>
      </div>

      <div style={{
        display:               "grid",
        gridTemplateColumns:   "repeat(auto-fill, minmax(260px, 1fr))",
        gap:                   "6px 14px",
      }}>
        {rows.map((r, i) => {
          const isMine    = myVote && (myVote.district || myVote.state) === r.state;
          const intensity = max ? r.share / max : 0;
          return (
            <div key={r.state} style={{
              display:               "grid",
              gridTemplateColumns:   "24px 1fr 50px",
              gap:                   8,
              alignItems:            "center",
              padding:               "6px 8px",
              background:            isMine ? K.yellow : "transparent",
              borderLeft:            `4px solid ${isMine ? K.ink : "transparent"}`,
            }}>
              <span style={{
                fontFamily: "'Archivo Black', sans-serif",
                fontSize:   "0.75rem",
                color:      K.inkSoft,
              }}>
                {i + 1}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize:      "0.82rem",
                  fontWeight:    600,
                  overflow:      "hidden",
                  textOverflow:  "ellipsis",
                  whiteSpace:    "nowrap",
                }}>
                  {r.state}
                </div>
                <div style={{
                  height:   6,
                  background: K.cream,
                  border:   `1px solid ${K.ink}`,
                  marginTop: 3,
                  position: "relative",
                  overflow: "hidden",
                }}>
                  <div style={{
                    position:   "absolute",
                    inset:      0,
                    width:      `${intensity * 100}%`,
                    background: K.roach,
                    transition: "width 500ms ease",
                  }} />
                </div>
              </div>
              <span style={{
                fontFamily: "'Archivo Black', sans-serif",
                fontSize:   "0.85rem",
                textAlign:  "right",
              }}>
                {r.share.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Share card (canvas 9:16) ──────────────────────────────────────────────────
function ShareCard({ open, onClose, party, myVote, tally }) {
  const canvasRef   = useRef(null);
  const [dl, setDl] = useState(false);

  const totals = useMemo(() => {
    const t = {};
    PARTIES.forEach(p => { t[p.id] = tally[p.id]?._total || 0; });
    return t;
  }, [tally]);
  const grand      = Object.values(totals).reduce((a, b) => a + b, 0);
  const sorted     = [...PARTIES].sort((a, b) => totals[b.id] - totals[a.id]);
  const voteState  = myVote?.district || myVote?.state || null;

  useEffect(() => {
    if (!open || !canvasRef.current || !party) return;

    const W = 1080, H = 1920;
    const c = canvasRef.current;
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");

    const draw = (logoImg) => {
      // background
      ctx.fillStyle = K.cream;
      ctx.fillRect(0, 0, W, H);

      // border
      ctx.strokeStyle = K.ink;
      ctx.lineWidth   = 16;
      ctx.strokeRect(40, 40, W - 80, H - 80);

      // UE logo
      if (logoImg) ctx.drawImage(logoImg, 100, 130, 180, 180);

      // wordmark
      ctx.fillStyle = K.navy;
      ctx.font      = "900 64px 'Archivo Black', sans-serif";
      ctx.fillText("Unofficial", 310, 210);
      ctx.fillStyle = K.saffron;
      ctx.fillText("Election", 310, 270);
      ctx.font      = "italic 28px 'IBM Plex Sans', sans-serif";
      ctx.fillStyle = K.inkSoft;
      ctx.fillText("the roach edition · 🪳", 310, 310);

      // my vote label
      ctx.fillStyle = K.red;
      ctx.font      = "900 48px 'Archivo Black', sans-serif";
      ctx.fillText("MY VOTE:", 100, 420);

      // party block
      ctx.fillStyle   = party.color;
      ctx.fillRect(100, 460, W - 200, 280);
      ctx.strokeStyle = K.ink;
      ctx.lineWidth   = 10;
      ctx.strokeRect(100, 460, W - 200, 280);

      ctx.fillStyle = K.cream;
      ctx.font      = "900 140px 'Archivo Black', sans-serif";
      ctx.fillText(party.short, 140, 620);
      ctx.font      = "500 36px 'IBM Plex Sans', sans-serif";
      ctx.fillText(party.en, 140, 680);
      ctx.font      = "500 32px 'Noto Sans Devanagari', sans-serif";
      ctx.fillText(party.hi, 140, 725);

      if (voteState) {
        ctx.fillStyle = K.ink;
        ctx.font      = "700 36px 'IBM Plex Sans', sans-serif";
        ctx.fillText(`📍 ${voteState}`, 100, 820);
      }

      // live standings
      ctx.fillStyle = K.ink;
      ctx.font      = "900 44px 'Archivo Black', sans-serif";
      ctx.fillText("LIVE STANDINGS", 100, 920);

      const top  = sorted.slice(0, 5);
      const barX = 100, barW = W - 200, rowH = 130;
      top.forEach((p, i) => {
        const y    = 970 + i * rowH;
        const pp   = pct(totals[p.id], grand);

        ctx.fillStyle = i === 0 ? K.red : K.inkSoft;
        ctx.font      = "900 44px 'Archivo Black', sans-serif";
        ctx.fillText(`${i + 1}`, barX, y + 30);

        ctx.fillStyle = K.ink;
        ctx.font      = "900 36px 'Archivo Black', sans-serif";
        ctx.fillText(p.short + (p.id === "CJP" ? " 🪳" : ""), barX + 70, y + 30);

        ctx.fillStyle  = i === 0 ? K.red : K.ink;
        ctx.font       = "900 40px 'Archivo Black', sans-serif";
        ctx.textAlign  = "right";
        ctx.fillText(`${pp.toFixed(1)}%`, barX + barW, y + 30);
        ctx.textAlign  = "left";

        ctx.fillStyle   = K.paper;
        ctx.fillRect(barX + 70, y + 50, barW - 70, 28);
        ctx.strokeStyle = K.ink;
        ctx.lineWidth   = 4;
        ctx.strokeRect(barX + 70, y + 50, barW - 70, 28);

        ctx.fillStyle = p.color;
        ctx.fillRect(barX + 70, y + 50, (barW - 70) * (pp / 100), 28);
      });

      // footer
      ctx.fillStyle = K.ink;
      ctx.font      = "700 32px 'IBM Plex Sans', sans-serif";
      ctx.fillText("unofficialelections.com", 100, H - 160);
      ctx.font      = "italic 24px 'IBM Plex Sans', sans-serif";
      ctx.fillStyle = K.inkSoft;
      ctx.fillText("unofficial · unverified · unserious", 100, H - 110);
      ctx.fillText("not affiliated with any party, real or roach-shaped", 100, H - 75);
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => draw(img);
    img.onerror = () => draw(null);
    img.src     = "/ue-logo.jpg";
  }, [open, party, myVote, tally, grand, sorted, totals, voteState]);

  if (!open) return null;

  const handleDownload = () => {
    const c = canvasRef.current;
    if (!c) return;
    const link   = document.createElement("a");
    link.download = "my-cjp-vote.png";
    link.href     = c.toDataURL("image/png");
    link.click();
    setDl(true);
    setTimeout(() => setDl(false), 2000);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(10,10,10,0.85)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: K.cream,
          border:     `4px solid ${K.ink}`,
          boxShadow:  `12px 12px 0 ${K.red}`,
          padding:    "1.5rem",
          maxWidth:   420,
          width:      "100%",
          position:   "relative",
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 10, right: 10,
            background: K.ink, color: K.cream,
            border: "none", width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={18} />
        </button>

        <h3 style={{
          fontFamily:    "'Archivo Black', sans-serif",
          fontSize:      "1.3rem",
          margin:        "0 0 12px",
          textTransform: "uppercase",
        }}>
          Your share card
        </h3>

        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "auto", border: `2px solid ${K.ink}`, display: "block", marginBottom: 14 }}
        />

        <button
          onClick={handleDownload}
          style={{
            width:         "100%",
            background:    K.ink,
            color:         K.cream,
            border:        "none",
            padding:       "14px",
            fontFamily:    "'Archivo Black', sans-serif",
            fontSize:      "1rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor:        "pointer",
            display:       "flex",
            alignItems:    "center",
            justifyContent:"center",
            gap:           10,
          }}
        >
          {dl
            ? <><Check size={18} /> Downloaded!</>
            : <><Download size={18} /> Download for Instagram</>
          }
        </button>

        <p style={{ fontSize: "0.72rem", color: K.inkSoft, marginTop: 10, textAlign: "center", fontStyle: "italic" }}>
          Post it. Tag @unofficialelections. Let the roach spread.
        </p>
      </div>
    </div>
  );
}

// ── Tab style helper ──────────────────────────────────────────────────────────
const tabStyle = (active) => ({
  padding:       "8px 16px",
  background:    active ? K.ink : "transparent",
  color:         active ? K.cream : K.ink,
  border:        `3px solid ${K.ink}`,
  fontFamily:    "'Archivo Black', sans-serif",
  fontSize:      "0.78rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  cursor:        "pointer",
});

// ── Vote screen ───────────────────────────────────────────────────────────────
function VoteScreen({ selected, setSelected, state, setState, error, submitting, onSubmit }) {
  return (
    <div>
      {/* Hero */}
      <div style={{ marginBottom: "2.5rem", maxWidth: 820 }}>
        <Stamp color={K.red} rotate={-4} style={{ marginBottom: 18 }}>
          🪳 Mock Ballot · सर्वेक्षण
        </Stamp>
        <h1 style={{
          fontFamily:    "'Archivo Black', sans-serif",
          fontSize:      "clamp(1.8rem, 8vw, 4.2rem)",
          lineHeight:    0.95,
          letterSpacing: "-0.02em",
          margin:        "0 0 1rem",
          textTransform: "uppercase",
          overflowWrap:  "break-word",
          wordBreak:     "break-word",
        }}>
          If the{" "}
          <span style={{ color: K.red, position: "relative" }}>
            cockroaches
            <svg
              viewBox="0 0 200 12"
              preserveAspectRatio="none"
              style={{ position: "absolute", left: 0, bottom: -6, width: "100%", height: 10 }}
            >
              <path d="M2,6 Q50,2 100,7 T198,5" stroke={K.red} strokeWidth="4" fill="none" strokeLinecap="round" />
            </svg>
          </span>{" "}
          ran in 2029 —
          <br />who's getting your vote?
        </h1>
        <p style={{ fontSize: "1.05rem", lineHeight: 1.5, color: K.inkSoft, maxWidth: 620 }}>
          A nationwide mock ballot. Pick one party, pick your state, see where the country actually stands.
          The official Election Commission won't be calling — but the timeline will.
        </p>

        <div style={{ marginTop: "1.25rem" }}>
          <CopyLinkButton />
        </div>

      </div>

      {/* Party grid — CJP spans full width; compact 3-col on mobile */}
      <style>{`
        .cjp-party-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.2rem;
          margin-bottom: 2.5rem;
        }
        .cjp-party-compact { display: none; }
        .cjp-party-full    { display: flex; }
        @media (max-width: 767px) {
          .cjp-party-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.5rem;
          }
          .cjp-party-compact { display: flex !important; }
          .cjp-party-full    { display: none !important; }
        }
      `}</style>
      <div className="cjp-party-grid">
        {PARTIES.map(p => (
          <div
            key={p.id}
            style={{ gridColumn: p.id === "CJP" ? "1 / -1" : "auto" }}
          >
            <PartyCard
              party={p}
              selected={selected?.id === p.id}
              onClick={() => { playClickSound(); setSelected(p); }}
            />
          </div>
        ))}
      </div>

      {/* State + submit box */}
      <div style={{
        background: K.paper,
        border:     `3px solid ${K.ink}`,
        boxShadow:  `6px 6px 0 ${K.ink}`,
        padding:    "1.6rem",
      }}>
        <label style={{
          display:       "block",
          fontFamily:    "'Archivo Black', sans-serif",
          fontSize:      "0.85rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          marginBottom:  10,
        }}>
          <MapPin size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
          Where are you voting from?
        </label>
        <select
          value={state}
          onChange={e => setState(e.target.value)}
          style={{
            width:               "100%",
            padding:             "12px 14px",
            background:          K.cream,
            border:              `3px solid ${K.ink}`,
            fontFamily:          "'IBM Plex Sans', sans-serif",
            fontSize:            "1rem",
            fontWeight:          600,
            color:               K.ink,
            cursor:              "pointer",
            appearance:          "none",
            backgroundImage:     `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='%230A0A0A' stroke-width='2' fill='none'/></svg>")`,
            backgroundRepeat:    "no-repeat",
            backgroundPosition:  "right 14px center",
          }}
        >
          <option value="">— select your state / UT —</option>
          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {error && (
          <div style={{
            marginTop: 14, padding: "10px 14px",
            background: K.red, color: K.cream,
            display: "flex", alignItems: "center", gap: 8,
            fontWeight: 600, border: `3px solid ${K.ink}`,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {(() => {
          const missingParty = !selected;
          const missingState = !state;
          const isDisabled   = missingParty || missingState || submitting;
          const readyColor   = selected ? selected.color : K.ink;
          return (
            <>
              <button
                onClick={onSubmit}
                disabled={isDisabled}
                style={{
                  marginTop:     18,
                  width:         "100%",
                  padding:       "18px",
                  background:    isDisabled ? K.inkSoft : readyColor,
                  color:         K.cream,
                  border:        `3px solid ${isDisabled ? K.inkSoft : K.ink}`,
                  boxShadow:     isDisabled ? "none" : `5px 5px 0 ${K.ink}`,
                  fontFamily:    "'Archivo Black', sans-serif",
                  fontSize:      "1.1rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  cursor:        isDisabled ? "not-allowed" : "pointer",
                  opacity:       isDisabled ? 0.45 : 1,
                  transition:    "background 200ms ease, opacity 150ms ease, box-shadow 150ms ease",
                }}
              >
                {submitting
                  ? "Casting…"
                  : missingParty
                    ? "← Pick a party first"
                    : missingState
                      ? "← Pick your state above"
                      : "Cast your vote 🗳️"}
              </button>
              <p style={{ marginTop: 10, fontSize: "0.72rem", color: K.inkSoft, fontStyle: "italic", textAlign: "center" }}>
                One vote per device. No login. No tracking. Just the roach and you.
              </p>
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ── Already voted screen ──────────────────────────────────────────────────────
function AlreadyVoted({ myVote, onSeeResults }) {
  const p = PARTY_BY_ID[myVote.party];
  const voteState = myVote.district || myVote.state;
  return (
    <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center", padding: "2rem 0" }}>
      <Stamp color={K.green} rotate={-3} style={{ marginBottom: 24 }}>
        ✓ Vote recorded
      </Stamp>
      <h2 style={{
        fontFamily:    "'Archivo Black', sans-serif",
        fontSize:      "2.2rem",
        textTransform: "uppercase",
        margin:        "0 0 1rem",
      }}>
        You voted for{" "}
        <span style={{ color: p.color, background: K.ink, padding: "2px 12px" }}>{p.short}</span>
      </h2>
      <p style={{ color: K.inkSoft, marginBottom: 24 }}>
        from <strong>{voteState}</strong>. One vote per device — see how the country answered.
      </p>
      <button
        onClick={onSeeResults}
        style={{
          background:    K.ink,
          color:         K.cream,
          border:        "none",
          padding:       "16px 28px",
          fontFamily:    "'Archivo Black', sans-serif",
          fontSize:      "1rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          cursor:        "pointer",
          boxShadow:     `5px 5px 0 ${K.red}`,
        }}
      >
        See live results →
      </button>
    </div>
  );
}

// ── Results screen ────────────────────────────────────────────────────────────
function CopyLinkButton() {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <button
      onClick={handleCopy}
      title="Copy link"
      style={{
        display:       "inline-flex",
        alignItems:    "center",
        gap:           6,
        padding:       "6px 14px",
        background:    copied ? K.green : "#25D366",
        color:         "#FFFFFF",
        border:        `2px solid ${K.ink}`,
        boxShadow:     copied ? "none" : `3px 3px 0 ${K.ink}`,
        fontFamily:    "'Archivo Black', sans-serif",
        fontSize:      "1rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        cursor:        "pointer",
        transition:    "background 200ms ease, box-shadow 200ms ease",
        flexShrink:    0,
        alignSelf:     "center",
      }}
    >
      {copied ? (
        <>
          <Check size={13} /> Copied!
        </>
      ) : (
        <>
          {/* WhatsApp icon */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Share
        </>
      )}
    </button>
  );
}

function ResultsScreen({ tally, myVote, totals, grand, sortedParties, onShare }) {
  const leader = sortedParties[0];
  return (
    <div>
      <div style={{ marginBottom: "2rem" }}>
        <Stamp color={K.green} rotate={-3} style={{ marginBottom: 16 }}>
          <TrendingUp size={12} style={{ display: "inline", marginRight: 4 }} /> Live · लाइव
        </Stamp>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <h1 style={{
            fontFamily:    "'Archivo Black', sans-serif",
            fontSize:      "clamp(2rem, 5vw, 3.5rem)",
            margin:        0,
            textTransform: "uppercase",
            lineHeight:    0.95,
          }}>
            {leader.short} is leading
          </h1>
          {leader.id === "CJP" && <CJPLogo size={42} style={{ marginLeft: 8 }} />}
          <CopyLinkButton />
        </div>
        <p style={{ marginTop: 12, color: K.inkSoft, fontSize: "1.05rem" }}>
          <strong style={{ color: K.ink }}>{fmt(grand)}</strong> votes cast nationwide.
          {myVote && (
            <>
              {" "}Your vote:{" "}
              <strong style={{ color: PARTY_BY_ID[myVote.party]?.color }}>{myVote.party}</strong>{" "}
              from <strong>{myVote.district || myVote.state}</strong>.
            </>
          )}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "2rem" }}>
        {/* National standings */}
        <div style={{
          background: K.paper,
          border:     `3px solid ${K.ink}`,
          boxShadow:  `6px 6px 0 ${K.ink}`,
          padding:    "1.6rem",
        }}>
          <h3 style={{
            fontFamily:    "'Archivo Black', sans-serif",
            fontSize:      "1.4rem",
            textTransform: "uppercase",
            margin:        "0 0 1rem",
          }}>
            National standings
          </h3>
          {sortedParties.map((p, i) => (
            <ResultBar
              key={p.id}
              party={p}
              count={totals[p.id]}
              total={grand}
              isMine={myVote?.party === p.id}
              rank={i + 1}
            />
          ))}

          <button
            onClick={onShare}
            style={{
              marginTop:     20,
              width:         "100%",
              background:    K.red,
              color:         K.cream,
              border:        `3px solid ${K.ink}`,
              boxShadow:     `5px 5px 0 ${K.ink}`,
              padding:       "14px",
              fontFamily:    "'Archivo Black', sans-serif",
              fontSize:      "1rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor:        "pointer",
              display:       "flex",
              alignItems:    "center",
              justifyContent:"center",
              gap:           10,
            }}
          >
            <Share2 size={18} /> Make my Instagram card
          </button>

          <a
            href={MANIFESTO_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              gap:            8,
              marginTop:      10,
              padding:        "10px",
              background:     "transparent",
              color:          K.roach,
              border:         `2px dashed ${K.roach}`,
              fontFamily:     "'Archivo Black', sans-serif",
              fontSize:       "0.78rem",
              letterSpacing:  "0.08em",
              textTransform:  "uppercase",
              textDecoration: "none",
            }}
          >
            <BookOpen size={14} /> 🪳 View CJP Manifesto <ExternalLink size={12} />
          </a>
        </div>

        {/* Subscribe CTA */}
        <SubscribeCTA
          theme="cream"
          title="NOTIFY ME WHEN CJP HITS 100K VOTES"
          subtitle="We'll email you when CJP hits 100,000 votes."
          source="cjp"
        />

        {/* State heatmap */}
        <div style={{
          background: K.paper,
          border:     `3px solid ${K.ink}`,
          boxShadow:  `6px 6px 0 ${K.ink}`,
          padding:    "1.6rem",
        }}>
          <StateHeatmap tally={tally} myVote={myVote} />
        </div>
      </div>
    </div>
  );
}

// ── Font styles + keyframes ───────────────────────────────────────────────────
function FontStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=IBM+Plex+Sans:wght@400;500;600;700&family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap');
      @keyframes cjp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      * { box-sizing: border-box; }
      select option { background: ${K.cream}; color: ${K.ink}; }
      ::selection { background: ${K.yellow}; color: ${K.ink}; }
    `}</style>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function CJPApp() {
  const navigate    = useNavigate();
  const [view,       setView]       = useState("vote");
  const [tally,      setTally]      = useState(emptyTally);
  const [myVote,     setMyVote]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const [state,      setState]      = useState("");
  const [error,      setError]      = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shareOpen,  setShareOpen]  = useState(false);

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const localVote = readLocalVote();
      const counts    = await fetchCounts("cjp").catch(() => ({}));
      setTally(supabaseToTally(counts));
      setMyVote(localVote);
      if (localVote) setView("results");
      setLoading(false);
    })();
  }, []);

  // ── Poll for new tallies on results screen ─────────────────────────────────
  useEffect(() => {
    if (view !== "results") return;
    const id = setInterval(async () => {
      const counts = await fetchCounts("cjp").catch(() => ({}));
      setTally(supabaseToTally(counts));
    }, 8000);
    return () => clearInterval(id);
  }, [view]);

  // ── Submit vote ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selected) { setError("Pick a party first."); return; }
    if (!state)    { setError("Pick your state — the roach needs to know."); return; }
    setError("");
    setSubmitting(true);
    playClickSound();

    const result = await castVote("cjp", state, selected.id);

    if (result.ok || result.duplicate) {
      const vote = { party: selected.id, district: state, ts: Date.now() };
      cacheVote("cjp", vote);

      // Optimistic local tally update — then immediately re-fetch from DB
      setMyVote(vote);
      setView("results");
      const counts = await fetchCounts("cjp").catch(() => ({}));
      setTally(supabaseToTally(counts));
    } else {
      setError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const totals = useMemo(() => {
    const t = {};
    PARTIES.forEach(p => { t[p.id] = tally[p.id]?._total || 0; });
    return t;
  }, [tally]);
  const grand         = Object.values(totals).reduce((a, b) => a + b, 0);
  const sortedParties = useMemo(
    () => [...PARTIES].sort((a, b) => totals[b.id] - totals[a.id]),
    [totals]
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: K.cream,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        <FontStyles />
        <div style={{ textAlign: "center" }}>
          <Loader2 size={32} style={{ animation: "cjp-spin 1s linear infinite" }} />
          <div style={{ marginTop: 12, fontFamily: "'Archivo Black', sans-serif", letterSpacing: "0.1em" }}>
            COUNTING ROACHES…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight:  "100vh",
      background: K.cream,
      color:      K.ink,
      fontFamily: "'IBM Plex Sans', sans-serif",
      position:   "relative",
      overflow:   "hidden",
    }}>
      <FontStyles />
      <GrainOverlay />

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header style={{
        position:     "relative",
        zIndex:       2,
        borderBottom: `4px solid ${K.ink}`,
        background:   K.paper,
        padding:      "1rem 1.4rem",
      }}>
        <div style={{
          maxWidth:       1080,
          margin:         "0 auto",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          gap:            12,
          flexWrap:       "nowrap",
        }}>
          {/* Brand */}
          <button
            onClick={() => navigate("/")}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 12, minWidth: 0, overflow: "hidden" }}
          >
            <img
              src="/ue-logo.jpg"
              alt="UnofficialElection logo"
              width={44}
              height={44}
              style={{ display: "block", flexShrink: 0, objectFit: "cover", borderRadius: 6, border: `2px solid ${K.ink}` }}
            />
            <div style={{ lineHeight: 1, textAlign: "left" }}>
              <div style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: "1.1rem", letterSpacing: "-0.01em", color: K.navy }}>
                Unofficial<span style={{ color: K.saffron }}>Election</span>
              </div>
              <div style={{ fontSize: "0.65rem", color: K.inkSoft, fontStyle: "italic", marginTop: 3, letterSpacing: "0.04em" }}>
                the roach edition · सर्वे
              </div>
            </div>
          </button>

          {/* Right: site nav + ballot tabs + all polls */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>

            {/* Site nav links — hidden on small screens */}
            <nav style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              {[
                { label: "How It Works", path: "/how-it-works" },
                { label: "Calendar",     path: "/election-calendar" },
                { label: "Blog",         path: "/blog" },
                { label: "FAQ",          path: "/faq" },
                { label: "About",        path: "/about" },
                { label: "Contact",      path: "/contact" },
              ].map(({ label, path }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  style={{
                    background:    "transparent",
                    border:        "none",
                    borderBottom:  `1.5px solid transparent`,
                    padding:       "2px 4px",
                    fontFamily:    "'IBM Plex Sans', sans-serif",
                    fontSize:      "0.72rem",
                    fontWeight:    500,
                    color:         K.inkSoft,
                    cursor:        "pointer",
                    letterSpacing: "0.01em",
                    transition:    "color 120ms ease, border-color 120ms ease",
                    display:       "none",
                  }}
                  className="cjp-nav-link"
                  onMouseEnter={e => { e.currentTarget.style.color = K.ink; e.currentTarget.style.borderBottomColor = K.ink; }}
                  onMouseLeave={e => { e.currentTarget.style.color = K.inkSoft; e.currentTarget.style.borderBottomColor = "transparent"; }}
                >
                  {label}
                </button>
              ))}
            </nav>

            {/* Separator */}
            <div className="cjp-nav-divider" style={{ width: 1, height: 20, background: K.inkSoft, opacity: 0.35, display: "none" }} />

            {/* Ballot / Results tabs — only once voted */}
            {myVote && (
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => setView("vote")}    style={tabStyle(view === "vote")}>Ballot</button>
                <button onClick={() => setView("results")} style={tabStyle(view === "results")}>Results</button>
              </div>
            )}

            {/* All polls */}
            <button
              onClick={() => navigate("/")}
              style={{
                ...tabStyle(false),
                display:    "flex",
                alignItems: "center",
                gap:        5,
                border:     `3px solid ${K.inkSoft}`,
                color:      K.inkSoft,
              }}
            >
              <ChevronLeft size={12} /> All polls
            </button>
          </div>
        </div>

        {/* Mobile nav row — full-width scrollable strip below */}
        <div style={{
          maxWidth:       1080,
          margin:         "0.75rem auto 0",
          display:        "flex",
          gap:            6,
          overflowX:      "auto",
          paddingBottom:  2,
        }} className="cjp-mobile-nav">
          {[
            { label: "How It Works", path: "/how-it-works" },
            { label: "Calendar",     path: "/election-calendar" },
            { label: "Blog",         path: "/blog" },
            { label: "FAQ",          path: "/faq" },
            { label: "About",        path: "/about" },
            { label: "Contact",      path: "/contact" },
          ].map(({ label, path }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              style={{
                flexShrink:    0,
                background:    "transparent",
                border:        "none",
                borderBottom:  `1.5px solid ${K.inkSoft}`,
                padding:       "2px 6px",
                fontFamily:    "'IBM Plex Sans', sans-serif",
                fontSize:      "0.72rem",
                fontWeight:    500,
                color:         K.inkSoft,
                cursor:        "pointer",
                whiteSpace:    "nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <style>{`
          @media (min-width: 768px) {
            .cjp-nav-link    { display: inline-flex !important; }
            .cjp-nav-divider { display: block !important; }
            .cjp-mobile-nav  { display: none !important; }
          }
          @media (max-width: 767px) {
            .cjp-nav-divider { display: none; }
          }
        `}</style>
      </header>

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <main style={{
        position: "relative",
        zIndex:   2,
        maxWidth: 1080,
        margin:   "0 auto",
        padding:  "2rem 1.4rem 4rem",
      }}>
        {view === "vote" && !myVote && (
          <VoteScreen
            selected={selected}
            setSelected={setSelected}
            state={state}
            setState={setState}
            error={error}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        )}

        {view === "vote" && myVote && (
          <AlreadyVoted myVote={myVote} onSeeResults={() => setView("results")} />
        )}

        {view === "vote" && (
          <div style={{ marginTop: "2rem" }}>
            <SubscribeCTA
              theme="cream"
              title="NOTIFY ME WHEN CJP HITS 100K VOTES"
              subtitle="We'll email you when CJP hits 100,000 votes."
              source="cjp"
            />
          </div>
        )}

        {view === "results" && (
          <ResultsScreen
            tally={tally}
            myVote={myVote}
            totals={totals}
            grand={grand}
            sortedParties={sortedParties}
            onShare={() => setShareOpen(true)}
          />
        )}

      </main>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer style={{
        position:   "relative",
        zIndex:     2,
        borderTop:  `4px solid ${K.ink}`,
        background: K.paper,
      }}>
        {/* Instagram CTA strip */}
        <div style={{
          borderBottom:   `2px solid ${K.ink}`,
          padding:        "1.2rem 1.4rem",
          display:        "flex",
          justifyContent: "center",
        }}>
          <a
            href="https://instagram.com/unofficialelections"
            target="_blank"
            rel="noreferrer"
            style={{
              display:        "inline-flex",
              alignItems:     "center",
              gap:            10,
              padding:        "11px 22px",
              background:     "rgba(10,8,7,0.82)",
              color:          "#FFFFFF",
              border:         "1px solid rgba(255,255,255,0.12)",
              borderRadius:   999,
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              fontFamily:     "'IBM Plex Sans', sans-serif",
              fontSize:       "0.88rem",
              fontWeight:     600,
              textDecoration: "none",
              transition:     "border-color 200ms ease, box-shadow 200ms ease",
              boxShadow:      "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#E1306C"; e.currentTarget.style.boxShadow = "0 0 20px rgba(225,48,108,0.3)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            {/* Instagram icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="#E1306C" stroke="none"/>
            </svg>
            Follow @unofficialelections for results
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </a>
        </div>

        {/* Link columns */}
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "2rem 1.4rem" }}>
          <div style={{
            display:             "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap:                 "2rem",
          }}>
            {/* Brand blurb */}
            <div style={{ gridColumn: "1 / -1", maxWidth: 340 }}>
              <div style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: "1rem", color: K.navy, marginBottom: 6 }}>
                Unofficial<span style={{ color: K.saffron }}>Election</span>
              </div>
              <p style={{ fontSize: "0.78rem", color: K.inkSoft, lineHeight: 1.6, margin: "0 0 4px" }}>
                India's free anonymous election polling platform.
              </p>
              <p style={{ fontSize: "0.72rem", color: K.inkSoft, fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
                Unofficial · Unverified · Unserious.<br />
                A satirical mock poll. Not affiliated with any party — real or roach-shaped.
              </p>
            </div>

            {/* Platform */}
            {[
              {
                group: "Platform",
                links: [
                  { label: "How It Works",      path: "/how-it-works" },
                  { label: "Election Calendar",  path: "/election-calendar" },
                  { label: "7 States in 2027",   path: "/2027" },
                ],
              },
              {
                group: "Polls",
                links: [
                  { label: "Tamil Nadu 2026",           path: "/tn" },
                  { label: "West Bengal 2026",          path: "/wb" },
                  { label: "Mock 2029 Lok Sabha",       path: "/cockroach-janata-party-elections" },
                ],
              },
              {
                group: "Learn",
                links: [
                  { label: "Blog",    path: "/blog" },
                  { label: "FAQ",     path: "/faq" },
                  { label: "About",   path: "/about" },
                  { label: "Contact", path: "/contact" },
                ],
              },
              {
                group: "Legal",
                links: [
                  { label: "Privacy Policy", path: "/privacy" },
                ],
              },
            ].map(({ group, links }) => (
              <div key={group}>
                <div style={{
                  fontFamily:    "'Archivo Black', sans-serif",
                  fontSize:      "0.62rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color:         K.ink,
                  borderBottom:  `2px solid ${K.ink}`,
                  paddingBottom: 6,
                  marginBottom:  12,
                }}>
                  {group}
                </div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {links.map(({ label, path }) => (
                    <li key={path}>
                      <button
                        onClick={() => navigate(path)}
                        style={{
                          background:  "none",
                          border:      "none",
                          padding:     0,
                          cursor:      "pointer",
                          fontFamily:  "'IBM Plex Sans', sans-serif",
                          fontSize:    "0.82rem",
                          fontWeight:  500,
                          color:       K.inkSoft,
                          textAlign:   "left",
                          transition:  "color 120ms ease",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = K.ink; }}
                        onMouseLeave={e => { e.currentTarget.style.color = K.inkSoft; }}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Copyright */}
          <div style={{
            marginTop:  "2rem",
            paddingTop: "1.2rem",
            borderTop:  `2px solid ${K.ink}`,
            display:    "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap:   "wrap",
            gap:        8,
          }}>
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "0.68rem", color: K.inkSoft }}>
              © unofficialelections.com · Unofficial · non-political · entertainment only.
            </span>
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "0.68rem", color: K.inkSoft, fontStyle: "italic" }}>
              Inspired by @cockroachjantaparty &amp; the meme that ate the timeline.
            </span>
          </div>
        </div>
      </footer>

      {/* ── Share modal ───────────────────────────────────────────────────── */}
      <ShareCard
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        party={myVote ? PARTY_BY_ID[myVote.party] : null}
        myVote={myVote}
        tally={tally}
      />
    </div>
  );
}
